const DEVICE_TOKENS_PATH = "/api/v1/device-tokens";

export interface DeviceTokenApiOptions {
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  /** Root directory base URL (e.g. "https://a3s-int.visioni.network") — NOT a realm URL. */
  directoryBaseUrl: string | (() => Promise<string>);
}

export interface DeviceTokenApi {
  isRegistered(token: string): Promise<boolean>;
  registerToken(token: string, platform: "ios" | "android"): Promise<void>;
  unregisterToken(token: string): Promise<void>;
}

export function createDeviceTokenApi({ authenticatedFetch, directoryBaseUrl }: DeviceTokenApiOptions): DeviceTokenApi {
  async function baseUrl(): Promise<string> {
    const url = typeof directoryBaseUrl === "function" ? await directoryBaseUrl() : directoryBaseUrl;
    return `${url}${DEVICE_TOKENS_PATH}`;
  }

  return {
    async isRegistered(token: string): Promise<boolean> {
      const url = `${await baseUrl()}?token=${encodeURIComponent(token)}`;
      const response = await authenticatedFetch(url, { method: "GET" });
      if (response.status === 404) return false;
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to check token registration: ${response.status.toString()} ${errorText}`);
      }
      const data = (await response.json()) as { registered: boolean };
      return data.registered;
    },

    async registerToken(token: string, platform: "ios" | "android"): Promise<void> {
      const url = await baseUrl();
      const response = await authenticatedFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, platform }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to register token: ${response.status.toString()} ${errorText}`);
      }
    },

    async unregisterToken(token: string): Promise<void> {
      const url = await baseUrl();
      const response = await authenticatedFetch(url, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to unregister token: ${response.status.toString()} ${errorText}`);
      }
    },
  };
}
